import { Autocomplete, TextField } from "@mui/material";
import { useAuth } from "context/AuthContext";
import { useDashboard } from "context/DashboardContext";
import { useFinance } from "context/FinanceContext";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  financeApi,
  payoutApi,
  settlementParams,
  settlementRows,
  validRejection,
} from "services/FinanceService";
import OperationsSummary from "./OperationsSummary";
import {
  Badge,
  DataTable,
  Field,
  FinancialChart,
  LoadState,
  Modal,
  Page,
  Panel,
  Stats,
  Unavailable,
  dateTime,
  localDate,
  money,
  useFinanceData,
} from "./shared";

const moneyColumn = (key, label) => ({
  key,
  label,
  render: (row) => money(row[key]),
});
const statusColumn = {
  key: "status",
  label: "Status",
  render: (row) => <Badge value={row.status} />,
};
const timeColumn = (key, label) => ({
  key,
  label,
  render: (row) => dateTime(row[key]),
});
const overviewFields = [
  ["totalMerchantPayables", "Merchant payables"],
  ["unsettledProviderReceivables", "Provider receivables"],
  ["settlementHoldingPool", "Settlement holding pool"],
  ["clearingTransitBalance", "Clearing in transit"],
  ["accumulatedFeeRevenue", "Accumulated fee revenue"],
  ["activeHoldsTotal", "Active holds"],
];

export function FinanceOverview() {
  const state = useFinanceData("/overview");
  const data = state.data;
  return (
    <Page
      title="Finance overview"
      description="Platform liquidity, merchant liabilities, and ledger health."
      actions={
        <button className="fin-button secondary" onClick={state.refresh}>
          Refresh
        </button>
      }
    >
      <LoadState {...state} />
      {data && (
        <>
          <Stats
            items={[
              ...overviewFields.map(([key, label]) => [
                label,
                money(data[key]),
              ]),
              [
                "Ledger status",
                <Badge
                  value={
                    data.ledgerBalanced == null
                      ? null
                      : data.ledgerBalanced
                      ? "BALANCED"
                      : "UNBALANCED"
                  }
                />,
              ],
              ["Discrepancies", data.activeDiscrepanciesCount ?? "—"],
            ]}
          />
          <FinancialChart
            title="Current financial balances"
            labels={overviewFields.map(([, label]) => label)}
            values={overviewFields.map(([key]) => data[key])}
          />
          <Panel title="Liquidity snapshot">
            <DataTable
              rowKey="name"
              columns={[
                { key: "name", label: "Financial position" },
                moneyColumn("amount", "Amount (NGN)"),
              ]}
              rows={overviewFields.map(([key, name]) => ({
                name,
                amount: data[key],
              }))}
            />
          </Panel>
        </>
      )}
      <OperationsSummary />
      <div className="finance-grid">
        <Unavailable title="Transaction volume history">
          Historical volume series are not supplied by the finance API.
        </Unavailable>
        <Unavailable title="Channel distribution">
          The finance API does not expose channel-level volumes.
        </Unavailable>
      </div>
    </Page>
  );
}

function useMutation(refresh) {
  const { token, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const lock = useRef(false);
  const currentToken = useRef(token);
  currentToken.current = token;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  async function submit(api, path, options, onSuccess) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      const data = await api(path, { ...options, token });
      if (!mounted.current || currentToken.current !== token) return;
      setResult(data);
      toast.success("Action completed. Review the returned status below.");
      onSuccess?.(data);
      refresh?.();
    } catch (err) {
      if (!mounted.current || currentToken.current !== token) return;
      setError(err.message);
      if (err.status === 401) logout();
      if (err.status === 400 || err.status === 409) refresh?.();
    } finally {
      lock.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return {
    busy,
    error,
    result,
    submit,
    reset: () => {
      setError(null);
      setResult(null);
    },
  };
}
function Outcome({ value }) {
  if (!value) return null;
  return (
    <Panel title="Latest action result">
      <div className="notice success" role="status">
        Request completed successfully.
      </div>
      <dl className="detail-grid">
        {Object.entries(value)
          .filter(([, v]) => typeof v !== "object" || v === null)
          .map(([key, v]) => (
            <div key={key}>
              <dt>{key.replace(/([A-Z])/g, " $1")}</dt>
              <dd>{v == null ? "—" : String(v)}</dd>
            </div>
          ))}
      </dl>
    </Panel>
  );
}
export function Settlements() {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [cutoffDate, setCutoffDate] = useState(localDate());
  const { user } = useAuth();
  const operator = user?.username || user?.email || user?.id;
  const state = useFinanceData(
    "/settlements",
    settlementParams(page, size, status)
  );
  const mutation = useMutation(state.refresh);
  const rows = settlementRows(state.data);
  const totalPages = state.data?.totalPages;
  const hasNext =
    totalPages != null
      ? page + 1 < totalPages
      : (Array.isArray(state.data) ? state.data.length : rows.length) >= size;
  const columns = [
    { key: "batchReference", label: "Batch reference" },
    { key: "customerId", label: "Customer ID" },
    { key: "merchantId", label: "Merchant ID" },
    { key: "merchantName", label: "Merchant" },
    moneyColumn("batchTotal", "Gross volume"),
    moneyColumn("merchantReceives", "Merchant receives"),
    moneyColumn("totalFee", "Total fee"),
    { key: "transactionCount", label: "Transactions" },
    { key: "destinationType", label: "Destination" },
    { key: "bankName", label: "Bank" },
    { key: "accountNumber", label: "Holding / destination account" },
    statusColumn,
    timeColumn("createdAt", "Calculated / created"),
    {
      key: "action",
      label: "Action",
      render: (row) => (
        <button
          className="fin-button secondary"
          disabled={
            !["PENDING", "QUEUED"].includes(row.status) || mutation.busy
          }
          onClick={() => {
            mutation.reset();
            setSelected(row);
          }}
        >
          Release
        </button>
      ),
    },
  ];
  return (
    <Page
      title="T+1 settlements"
      description="Review settlement batches and authorize pending releases."
      actions={
        <button className="fin-button secondary" onClick={state.refresh}>
          Refresh
        </button>
      }
    >
      <Panel>
        <div className="toolbar">
          <Field
            label="Status"
            value={status}
            options={[
              "",
              "PENDING",
              "PROCESSING",
              "DISBURSED",
              "FAILED",
              "QUEUED",
            ]}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(0);
            }}
          />
          <Field
            label="Page size"
            value={size}
            options={["10", "20", "50", "100"]}
            onChange={(e) => {
              setSize(Number(e.target.value));
              setPage(0);
            }}
          />
        </div>
      </Panel>
      <LoadState {...state} />
      {state.data && (
        <Panel title="Settlement batches">
          <DataTable columns={columns} rows={rows} rowKey="batchReference" />
          <div className="pagination">
            <span>
              {state.data.totalElements != null
                ? `${state.data.totalElements} batches`
                : `${rows.length} batches on this page`}{" "}
              · Page {page + 1}
              {totalPages != null && ` of ${Math.max(1, totalPages)}`}
            </span>
            <button
              className="fin-button secondary"
              disabled={page === 0 || state.loading}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <button
              className="fin-button secondary"
              disabled={state.loading || !hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </Panel>
      )}
      <Outcome value={mutation.result} />
      {selected && (
        <Modal
          title="Authorize settlement release"
          busy={mutation.busy}
          disabled={!operator || !cutoffDate}
          onClose={() => setSelected(null)}
          action="Authorize release"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.submit(
              financeApi,
              "/settlements/t1-release",
              {
                method: "POST",
                data: {
                  batchReference: selected.batchReference,
                  approvedBy: String(operator),
                  cutoffDate,
                },
              },
              () => setSelected(null)
            );
          }}
        >
          <p>
            Release batch <strong>{selected.batchReference}</strong> for{" "}
            {selected.merchantName || selected.merchantId}?
          </p>
          <p className="notice warning">
            This authorizes a merchant payout of{" "}
            {money(selected.merchantReceives ?? selected.batchTotal)}.{" "}
            {selected.holdingAccountNumber
              ? "Holding account"
              : "Destination account"}
            : {selected.accountNumber || "unavailable"}.
            {selected.bankName && ` Bank: ${selected.bankName}.`}
          </p>
          <Field
            label="Cutoff date"
            type="date"
            required
            value={cutoffDate}
            onChange={(e) => setCutoffDate(e.target.value)}
          />
          <p>Approved by: {operator || "Operator identifier unavailable"}</p>
          {mutation.error && (
            <p className="notice error" role="alert">
              {mutation.error}
            </p>
          )}
        </Modal>
      )}
    </Page>
  );
}

const accountTypes = ["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE"];
const accountCategories = [
  "BANK_HOLDING",
  "MERCHANT_WALLET",
  "CLEARING",
  "SETTLEMENT",
  "ASSET",
  "LIABILITY",
  "EQUITY",
  "REVENUE",
  "EXPENSE",
];
export function ChartOfAccounts() {
  const state = useFinanceData("/coa");
  const mutation = useMutation(state.refresh);
  const [form, setForm] = useState(null);
  const [toggle, setToggle] = useState(null);
  const [query, setQuery] = useState("");
  const [accountType, setAccountType] = useState("");
  const [category, setCategory] = useState("");
  const [activity, setActivity] = useState("");
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const rows = (state.data || []).filter(
    (row) =>
      `${row.code} ${row.name} ${row.type} ${row.category}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (!accountType || row.type === accountType) &&
      (!category || row.category === category) &&
      (!activity || row.active === (activity === "ACTIVE"))
  );
  return (
    <Page
      title="Chart of accounts"
      description="Manage the platform’s double-entry account classifications."
      actions={
        <button
          className="fin-button"
          onClick={() => {
            mutation.reset();
            setForm({
              code: "",
              name: "",
              type: "ASSET",
              category: "BANK_HOLDING",
              description: "",
            });
          }}
        >
          Add account
        </button>
      }
    >
      <Panel>
        <div className="toolbar">
          <Field
            label="Search accounts"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Field
            label="Accounting type"
            value={accountType}
            options={["", ...accountTypes]}
            onChange={(e) => setAccountType(e.target.value)}
          />
          <Field
            label="Business category"
            value={category}
            options={["", ...accountCategories]}
            onChange={(e) => setCategory(e.target.value)}
          />
          <Field
            label="Account status"
            value={activity}
            options={["", "ACTIVE", "INACTIVE"]}
            onChange={(e) => setActivity(e.target.value)}
          />
        </div>
      </Panel>
      <LoadState {...state} />
      {state.data && (
        <Panel title="Ledger accounts">
          <DataTable
            rowKey="code"
            rows={rows}
            columns={[
              { key: "code", label: "GL code" },
              { key: "name", label: "Account name" },
              { key: "normalBalance", label: "Normal Balance" },
              { key: "category", label: "Category" },
              { key: "description", label: "Description" },
              {
                key: "active",
                label: "Status",
                render: (row) => (
                  <Badge value={row.active ? "ACTIVE" : "INACTIVE"} />
                ),
              },
              timeColumn("createdAt", "Created"),
              {
                key: "action",
                label: "Action",
                render: (row) => (
                  <button
                    className="fin-button secondary"
                    disabled={mutation.busy}
                    onClick={() => {
                      mutation.reset();
                      setToggle(row);
                    }}
                  >
                    {row.active ? "Deactivate" : "Activate"}
                  </button>
                ),
              },
            ]}
          />
        </Panel>
      )}
      <Outcome value={mutation.result} />
      {form && (
        <Modal
          title="Create ledger account"
          busy={mutation.busy}
          onClose={() => setForm(null)}
          onSubmit={(e) => {
            e.preventDefault();
            mutation.submit(
              financeApi,
              "/coa",
              {
                method: "POST",
                data: {
                  ...form,
                  code: form.code.trim(),
                  name: form.name.trim(),
                },
              },
              () => setForm(null)
            );
          }}
          disabled={!form.code.trim() || !form.name.trim()}
        >
          <p className="notice warning" role="note">
            Chart for GL code guide:
            <br />
            1xxx — ASSETS — Normal Balance: DR
            <br />
            2xxx — LIABILITIES — Normal Balance: CR
            <br />
            3xxx — EQUITY — Normal Balance: CR
            <br />
            4xxx — REVENUE — Normal Balance: CR
            <br />
            5xxx — EXPENSES — Normal Balance: DR
          </p>
          <Field
            label="Unique GL code"
            name="code"
            value={form.code}
            onChange={change}
            required
          />
          <Field
            label="Account name"
            name="name"
            value={form.name}
            onChange={change}
            required
          />
          <Field
            label="Accounting type"
            name="type"
            value={form.type}
            onChange={change}
            options={accountTypes}
          />
          <Field
            label="Category"
            name="category"
            value={form.category}
            onChange={change}
            options={accountCategories}
          />
          <Field
            label="Description"
            name="description"
            value={form.description}
            onChange={change}
          />
          {mutation.error && (
            <p className="notice error" role="alert">
              {mutation.error}
            </p>
          )}
        </Modal>
      )}
      {toggle && (
        <Modal
          title={`${toggle.active ? "Deactivate" : "Activate"} account`}
          busy={mutation.busy}
          action="Confirm status change"
          onClose={() => setToggle(null)}
          onSubmit={(e) => {
            e.preventDefault();
            mutation.submit(
              financeApi,
              `/coa/${encodeURIComponent(toggle.code)}/status`,
              { method: "PATCH", params: { active: !toggle.active } },
              () => setToggle(null)
            );
          }}
        >
          <p>
            Change the status of {toggle.code} — {toggle.name}?
          </p>
          {mutation.error && (
            <p className="notice error" role="alert">
              {mutation.error}
            </p>
          )}
        </Modal>
      )}
    </Page>
  );
}

function SearchablePricingSelect({
  label,
  options,
  value,
  onChange,
  required = false,
  loading = false,
}) {
  return (
    <Autocomplete
      options={options}
      value={
        options.find((option) => option.value === String(value ?? "")) || null
      }
      onChange={(_, option) => onChange(option?.value || "")}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            {option.label}
          </li>
        );
      }}
      loading={loading}
      disableClearable={required}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          size="small"
          sx={{
            "& .MuiInputLabel-root": { fontSize: "0.8125rem" },
            "& .MuiInputBase-input": { fontSize: "0.8125rem" },
          }}
        />
      )}
    />
  );
}

const defaultRule = {
  ruleType: "GLOBAL_DEFAULT",
  channel: "ALL",
  provider: "ALL",
  merchantId: "",
  feeMode: "PERCENTAGE",
  feePercentage: "1.5",
  fixedFee: "0",
  capAmount: "",
  feeBearer: "MERCHANT",
};
export function PricingRules() {
  const {
    allMerchants,
    allMerchantError,
    loading: merchantsLoading,
    fetchAllMerchant,
  } = useDashboard();
  const merchantOptions = (allMerchants || [])
    .filter((row) => row.merchant?.id != null)
    .map(({ merchant }) => ({
      value: String(merchant.id),
      label: `${merchant.name || "Merchant"} (${merchant.id})`,
    }));
  const state = useFinanceData("/pricing/rules");
  const mutation = useMutation(state.refresh);
  const [form, setForm] = useState(null);
  const [override, setOverride] = useState(false);
  const [query, setQuery] = useState("");
  const [ruleType, setRuleType] = useState("");
  const [bearer, setBearer] = useState("");
  const rules = state.data || [];
  const visibleRules = rules.filter(
    (row) =>
      `${row.merchantId ?? ""} ${row.channel} ${row.provider} ${row.ruleType}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (!ruleType || row.ruleType === ruleType) &&
      (!bearer || row.feeBearer === bearer)
  );
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const open = (rule, custom) => {
    mutation.reset();
    setOverride(custom);
    setForm({
      ...defaultRule,
      ...rule,
      feeMode:
        Number(rule.fixedFee) > 0 && !Number(rule.feePercentage)
          ? "FIXED"
          : "PERCENTAGE",
    });
  };
  return (
    <Page
      title="Pricing & fee rules"
      description="CFS standard pricing, provider benchmarks, and merchant agreements."
      actions={
        <>
          <button
            className="fin-button secondary"
            onClick={() => open({}, true)}
          >
            Merchant override
          </button>
          <button className="fin-button" onClick={() => open({}, false)}>
            Add pricing rule
          </button>
        </>
      }
    >
      <Panel>
        <div className="toolbar">
          <Field
            label="Search merchant, channel or provider"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Field
            label="Pricing rule type"
            value={ruleType}
            options={["", ...new Set(rules.map((rule) => rule.ruleType))]}
            onChange={(e) => setRuleType(e.target.value)}
          />
          <Field
            label="Filter fee bearer"
            value={bearer}
            options={["", "MERCHANT", "CUSTOMER"]}
            onChange={(e) => setBearer(e.target.value)}
          />
        </div>
      </Panel>
      <LoadState {...state} />
      {state.data && (
        <Panel title="Configured rules">
          <DataTable
            rowKey="id"
            rows={visibleRules}
            columns={[
              { key: "id", label: "ID" },
              { key: "ruleType", label: "Rule type" },
              { key: "channel", label: "Channel" },
              { key: "provider", label: "Provider" },
              { key: "merchantId", label: "Merchant ID" },
              {
                key: "feePercentage",
                label: "Percentage",
                render: (row) => `${row.feePercentage}%`,
              },
              moneyColumn("fixedFee", "Fixed fee"),
              moneyColumn("capAmount", "Cap"),
              { key: "feeBearer", label: "Fee bearer" },
              // { key: "updatedBy", label: "Updated by" },
              timeColumn("updatedAt", "Updated"),
              {
                key: "active",
                label: "Status",
                render: (row) => (
                  <Badge value={row.active ? "ACTIVE" : "INACTIVE"} />
                ),
              },
              {
                key: "action",
                label: "Action",
                render: (row) => (
                  <button
                    className="fin-button secondary"
                    onClick={() =>
                      open(row, row.ruleType === "MERCHANT_OVERRIDE")
                    }
                  >
                    Edit
                  </button>
                ),
              },
            ]}
          />
        </Panel>
      )}
      <Outcome value={mutation.result} />
      {form && (
        <Modal
          title={override ? "Merchant pricing override" : "Save pricing rule"}
          busy={mutation.busy}
          disabled={
            override &&
            !merchantOptions.some(
              (option) => option.value === String(form.merchantId)
            )
          }
          onClose={() => setForm(null)}
          onSubmit={(e) => {
            e.preventDefault();
            if (
              override &&
              !merchantOptions.some(
                (option) => option.value === String(form.merchantId)
              )
            )
              return;
            const rates = {
              feePercentage:
                form.feeMode === "PERCENTAGE" ? Number(form.feePercentage) : 0,
              fixedFee: form.feeMode === "FIXED" ? Number(form.fixedFee) : 0,
              capAmount:
                form.capAmount === "" || form.capAmount == null
                  ? null
                  : Number(form.capAmount),
              ...(override
                ? {
                    merchantId: String(form.merchantId).trim(),
                    feeBearer: form.feeBearer,
                  }
                : {}),
            };
            mutation.submit(
              financeApi,
              override ? "/pricing/rules/merchant-override" : "/pricing/rules",
              {
                method: "POST",
                data: override
                  ? rates
                  : {
                      ...rates,
                      ruleType: form.ruleType,
                      channel: form.channel.trim(),
                      provider: form.provider.trim(),
                    },
              },
              () => setForm(null)
            );
          }}
        >
          {!override && (
            <>
              <Field
                label="Rule type"
                name="ruleType"
                value={form.ruleType}
                onChange={change}
                options={["GLOBAL_DEFAULT", "PROVIDER_COST"]}
              />
              <Field
                label="Channel"
                name="channel"
                required
                options={["ALL", "USSD", "TRANSFER", "CARD"]}
                value={form.channel}
                onChange={change}
              />
              <Field
                label="Provider"
                name="provider"
                required
                options={["ALL", "INTERSWITCH", "CORALPAY", "NIBSS"]}
                value={form.provider}
                onChange={change}
              />
            </>
          )}
          {override && (
            <>
              <SearchablePricingSelect
                label="Merchant"
                required
                loading={merchantsLoading}
                options={merchantOptions}
                value={form.merchantId}
                onChange={(merchantId) => setForm({ ...form, merchantId })}
              />
              {allMerchantError && (
                <p className="notice error" role="alert">
                  {allMerchantError}{" "}
                  <button
                    type="button"
                    className="table-link"
                    onClick={fetchAllMerchant}
                  >
                    Retry merchant list
                  </button>
                </p>
              )}
            </>
          )}
          <label className="fin-field">
            <span>Fee mode</span>
            <select
              name="feeMode"
              required
              value={form.feeMode}
              onChange={change}
            >
              <option value="PERCENTAGE">Percentage</option>
              <option value="FIXED">Fixed fee</option>
            </select>
          </label>
          {form.feeMode === "PERCENTAGE" ? (
            <Field
              label="Fee percentage"
              name="feePercentage"
              type="number"
              min="0"
              step="0.0001"
              required
              value={form.feePercentage}
              onChange={change}
            />
          ) : (
            <Field
              label="Fixed fee (NGN)"
              name="fixedFee"
              type="number"
              min="0"
              step="0.01"
              required
              value={form.fixedFee}
              onChange={change}
            />
          )}
          <Field
            label="Cap (NGN, optional)"
            name="capAmount"
            type="number"
            min="0"
            step="0.01"
            value={form.capAmount}
            onChange={change}
          />
          {mutation.error && (
            <p className="notice error" role="alert">
              {mutation.error}
            </p>
          )}
        </Modal>
      )}
    </Page>
  );
}

export function IncomeStatement() {
  const today = localDate();
  const [range, setRange] = useState({
    startDate: `${today.slice(0, 7)}-01`,
    endDate: today,
  });
  const [draft, setDraft] = useState(range);
  const state = useFinanceData("/reports/income-statement", range);
  const data = state.data;
  const providers = Object.entries(data?.providerExpenseBreakdown || {}).map(
    ([provider, amount]) => ({ provider, amount })
  );
  return (
    <Page
      title="Profit & loss"
      description="Income statement and provider costs for the selected reporting period."
    >
      <Panel>
        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            setRange(draft);
            if (
              draft.startDate === range.startDate &&
              draft.endDate === range.endDate
            )
              state.refresh();
          }}
        >
          <Field
            label="Start date"
            type="date"
            required
            max={draft.endDate}
            value={draft.startDate}
            onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
          />
          <Field
            label="End date"
            type="date"
            required
            min={draft.startDate}
            value={draft.endDate}
            onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
          />
          <button
            className="fin-button"
            disabled={
              !draft.startDate ||
              !draft.endDate ||
              draft.startDate > draft.endDate
            }
          >
            Generate report
          </button>
        </form>
      </Panel>
      <LoadState {...state} />
      {data && (
        <>
          <p className="report-period">
            Reporting period: {data.startDate || range.startDate} —{" "}
            {data.endDate || range.endDate}
          </p>
          <Stats
            items={[
              ["Gross MDR revenue", money(data.grossMdrRevenue)],
              [
                "Provider interchange expense",
                money(data.totalProviderInterchangeExpense),
              ],
              ["Net platform margin", money(data.netPlatformMargin)],
            ]}
          />
          <div className="finance-grid">
            <FinancialChart
              title="Revenue, cost & margin"
              labels={["Revenue", "Expense", "Net margin"]}
              values={[
                data.grossMdrRevenue,
                data.totalProviderInterchangeExpense,
                data.netPlatformMargin,
              ]}
            />
            {providers.length > 0 ? (
              <FinancialChart
                title="Provider expenses"
                type="doughnut"
                labels={providers.map((p) => p.provider)}
                values={providers.map((p) => p.amount)}
              />
            ) : (
              <Unavailable title="Provider expenses">
                No provider breakdown is available for this period.
              </Unavailable>
            )}
          </div>
          <Panel title="Provider expense breakdown">
            <DataTable
              rowKey="provider"
              rows={providers}
              columns={[
                { key: "provider", label: "Provider" },
                moneyColumn("amount", "Expense (NGN)"),
              ]}
            />
          </Panel>
        </>
      )}
    </Page>
  );
}

export function Reconciliation() {
  const state = useFinanceData("/overview");
  return (
    <Page
      title="Reconciliation"
      description="Monitor ledger alignment and platform discrepancies."
    >
      <LoadState {...state} />
      {state.data && (
        <Stats
          items={[
            [
              "Ledger status",
              <Badge
                value={
                  state.data.ledgerBalanced == null
                    ? null
                    : state.data.ledgerBalanced
                    ? "BALANCED"
                    : "UNBALANCED"
                }
              />,
            ],
            ["Discrepancies", state.data.activeDiscrepanciesCount ?? "—"],
            ["Active holds", money(state.data.activeHoldsTotal)],
          ]}
        />
      )}
      <Unavailable title="Reconciliation batches">
        Batch reconciliation is not available through the documented admin
        gateway. Account matching, variance details, and batch submission will
        become available when an authenticated admin integration is provided.
      </Unavailable>
    </Page>
  );
}
export function SwitchPerformance() {
  return (
    <Page
      title="Switch performance"
      description="Provider performance and processing health."
    >
      <Unavailable title="Provider comparison">
        Provider volumes, success rates, latency, and switch comparison charts
        are not supplied by the available admin APIs.
      </Unavailable>
    </Page>
  );
}

export function PayoutApprovals() {
  const { queue, busy, error, refresh } = useFinance();
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const mutation = useMutation(refresh);
  const open = (row, action) => {
    mutation.reset();
    setReason("");
    setSelected({ row, action });
  };
  const rows = queue.filter((row) =>
    `${row.merchantId} ${row.reference} ${row.transactionId} ${
      row.narration || ""
    }`
      .toLowerCase()
      .includes(query.toLowerCase())
  );
  const activePage = Math.min(
    page,
    Math.max(0, Math.ceil(rows.length / 20) - 1)
  );
  const columns = [
    { key: "transactionId", label: "Transaction ID" },
    { key: "merchantId", label: "Merchant ID" },
    { key: "type", label: "Type" },
    statusColumn,
    moneyColumn("amount", "Principal"),
    moneyColumn("fee", "Fee"),
    {
      key: "total",
      label: "Reserved debit",
      render: (row) => money(Number(row.amount) + Number(row.fee || 0)),
    },
    moneyColumn("balanceBefore", "Balance before"),
    moneyColumn("balanceAfter", "Balance after"),
    { key: "reference", label: "Reference" },
    { key: "narration", label: "Narration" },
    { key: "gatewayResponse", label: "Gateway response" },
    timeColumn("createdAt", "Created"),
    timeColumn("updatedAt", "Updated"),
    {
      key: "actions",
      label: "Review",
      render: (row) => (
        <div className="toolbar">
          <button
            className="fin-button"
            disabled={mutation.busy || row.status !== "PENDING_APPROVAL"}
            onClick={() => open(row, "approve")}
          >
            Approve
          </button>
          <button
            className="fin-button secondary"
            disabled={mutation.busy || row.status !== "PENDING_APPROVAL"}
            onClick={() => open(row, "reject")}
          >
            Reject
          </button>
        </div>
      ),
    },
  ];
  return (
    <Page
      title="Payout approvals"
      description="Dual custody review. Pending payouts have principal and fees reserved."
      actions={
        <button className="fin-button secondary" onClick={refresh}>
          Refresh queue
        </button>
      }
    >
      <Stats
        items={[
          ["Pending approvals", error ? "—" : queue.length],
          [
            "Reserved principal",
            error
              ? "—"
              : money(
                  queue.reduce((sum, row) => sum + Number(row.amount || 0), 0)
                ),
          ],
          [
            "Reserved fees",
            error
              ? "—"
              : money(
                  queue.reduce((sum, row) => sum + Number(row.fee || 0), 0)
                ),
          ],
        ]}
      />
      <Panel>
        <Field
          label="Search merchant, reference or transaction"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
        />
      </Panel>
      <LoadState
        loading={busy && queue.length === 0}
        error={error}
        refresh={refresh}
      />
      {!error && (
        <Panel title="Pending disbursements">
          <DataTable
            rows={rows.slice(activePage * 20, activePage * 20 + 20)}
            rowKey="transactionId"
            columns={columns}
          />
          <div className="pagination">
            <span>
              {rows.length} payouts · Page {activePage + 1}
            </span>
            <button
              className="fin-button secondary"
              disabled={activePage === 0}
              onClick={() => setPage(activePage - 1)}
            >
              Previous
            </button>
            <button
              className="fin-button secondary"
              disabled={(activePage + 1) * 20 >= rows.length}
              onClick={() => setPage(activePage + 1)}
            >
              Next
            </button>
          </div>
        </Panel>
      )}
      <Outcome value={mutation.result} />
      {selected && (
        <Modal
          title={
            selected.action === "approve" ? "Approve payout" : "Reject payout"
          }
          busy={mutation.busy}
          danger={selected.action === "reject"}
          action={
            selected.action === "approve"
              ? "Confirm disbursement"
              : "Reject and restore funds"
          }
          disabled={selected.action === "reject" && !validRejection(reason)}
          onClose={() => setSelected(null)}
          onSubmit={(e) => {
            e.preventDefault();
            if (selected.action === "reject" && !validRejection(reason)) return;
            mutation.submit(
              payoutApi,
              `/${encodeURIComponent(selected.row.transactionId)}/${
                selected.action
              }`,
              {
                method: "POST",
                data:
                  selected.action === "reject" ? { reason: reason.trim() } : {},
              },
              () => setSelected(null)
            );
          }}
        >
          <dl className="detail-grid">
            {[
              ["Transaction", selected.row.transactionId],
              [
                "Merchant",
                selected.row.merchantName || selected.row.merchantId,
              ],
              ["Reference", selected.row.reference],
              ["Principal", money(selected.row.amount)],
              ["Fee", money(selected.row.fee)],
              [
                "Total reserved",
                money(
                  Number(selected.row.amount) + Number(selected.row.fee || 0)
                ),
              ],
              ["Beneficiary", selected.row.beneficiaryName || "Not supplied"],
              ["Bank", selected.row.bankName || "Not supplied"],
              ["Account", selected.row.accountNumber || "Not supplied"],
            ].map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          {selected.action === "approve" ? (
            <p className="notice warning">
              Approving will disburse {money(selected.row.amount)} to the
              beneficiary bank account. This action cannot be reversed.
            </p>
          ) : (
            <>
              <p>
                Rejection restores the reserved principal and fee to the
                merchant wallet.
              </p>
              <label className="fin-field">
                <span>Audit justification (at least 5 characters)</span>
                <textarea
                  required
                  minLength={5}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                />
              </label>
            </>
          )}
          {mutation.error && (
            <p className="notice error" role="alert">
              {mutation.error}
            </p>
          )}
        </Modal>
      )}
    </Page>
  );
}
